import React from "react";
import {
  AlertTriangle, FileSearch, Target, Users, Boxes, Handshake,
  Rocket, Zap, LayoutGrid, ShieldCheck, Sparkles, CheckCircle2,
  Share2, Mail, Phone, Table2, FileText, MinusCircle, Search, ArrowRight,
} from "lucide-react";
import { useLang } from "../../i18n";
import { Reveal, SectionHeading } from "./shared";

const STEP_ICONS = [FileSearch, Target, Users, Boxes, Handshake];
const BENEFIT_ICONS = [Rocket, Users, Zap, LayoutGrid, ShieldCheck];
const CHAOS_ICONS = [Share2, Mail, Phone, Table2, FileText];
const OVERVIEW_ICONS = [Search, Handshake, LayoutGrid];

/* ============================ Problem ============================ */
export const ProblemSection = () => {
  const { t } = useLang();
  const p = t.problem;
  return (
    <section id="problem" className="border-y border-[var(--border)] section-wash">
      <div className="mx-auto max-w-[1180px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <Reveal><SectionHeading eyebrow={p.eyebrow} title={p.title} accent={p.accent} /></Reveal>
        <Reveal delay={0.05}>
          <p className="mx-auto mt-6 max-w-2xl text-center text-lg font-medium leading-relaxed text-[var(--ink)] sm:text-xl">{p.lead}</p>
        </Reveal>

        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
          {/* Missing capabilities */}
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-[var(--border)] bg-white p-6 shadow-md-soft">
              <div className="flex items-center gap-2 text-[var(--slate)]">
                <MinusCircle className="h-4 w-4 text-[var(--blue)]" strokeWidth={2} />
                <span className="text-xs font-semibold uppercase tracking-[0.1em]">{p.gapsLabel}</span>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {p.gaps.map((g) => (
                  <span key={g} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[var(--border)] bg-[var(--surface-2)]/60 px-3 py-1.5 text-[13px] font-medium text-[var(--ink)]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[var(--blue)]/50" />{g}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Fragmented status-quo */}
          <Reveal delay={0.12}>
            <div className="h-full rounded-2xl border border-[var(--border)] bg-white p-6 shadow-md-soft">
              <div className="flex items-center gap-2 text-[var(--slate)]">
                <Share2 className="h-4 w-4 text-[var(--blue)]" strokeWidth={2} />
                <span className="text-xs font-semibold uppercase tracking-[0.1em]">{p.chaosLabel}</span>
              </div>
              <div className="mt-5 grid grid-cols-5 gap-2">
                {p.chaos.map((c, i) => {
                  const Icon = CHAOS_ICONS[i];
                  return (
                    <div key={c} className="flex flex-col items-center gap-1.5 rounded-xl border border-[var(--border)] bg-[var(--surface-2)]/50 px-1.5 py-3 text-center">
                      <Icon className="h-4 w-4 text-[var(--slate)]" strokeWidth={1.7} />
                      <span className="text-[10px] font-medium leading-tight text-[var(--slate)]">{c}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.16}>
          <div className="mx-auto mt-6 flex max-w-4xl items-start gap-3 rounded-2xl border border-[var(--border)] border-l-4 border-l-[var(--blue)] bg-white p-5 shadow-md-soft">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--blue)]" strokeWidth={1.8} />
            <p className="text-[15px] font-medium leading-relaxed text-[var(--ink)]">{p.highlight}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ===================== Solution + How it works ===================== */
export const SolutionSection = () => {
  const { t } = useLang();
  const s = t.solution;
  return (
    <section id="solution" className="mx-auto max-w-[1180px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <Reveal><SectionHeading eyebrow={s.eyebrow} title={s.title} accent={s.accent} subline={s.subline} /></Reveal>

      <Reveal delay={0.08} className="mt-12">
        <div className="flex items-center justify-center gap-3">
          <span className="h-px w-8 bg-[var(--border)]" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-[0.12em] text-[var(--slate)]">{s.stepsTitle}</h3>
          <span className="h-px w-8 bg-[var(--border)]" />
        </div>
        <div className="relative mt-8">
          <div className="pointer-events-none absolute inset-x-[10%] top-[46px] hidden h-px bg-[var(--border)] lg:block" />
          <div className="relative grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {s.steps.map((step, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <div key={step.t} className="group relative flex flex-col rounded-2xl border border-[var(--border)] bg-white p-5 shadow-md-soft transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--blue)]/25 hover:shadow-lg-soft">
                  <div className="flex items-center justify-between">
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--blue-soft)] text-[var(--blue)] transition-transform duration-300 group-hover:scale-[1.06]"><Icon className="h-5 w-5" strokeWidth={1.7} /></span>
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--ink)] font-heading text-xs font-bold text-white">{i + 1}</span>
                  </div>
                  <div className="mt-4 font-heading text-[15px] font-bold text-[var(--ink)]">{step.t}</div>
                  <div className="mt-1.5 text-[13px] leading-snug text-[var(--slate)]">{step.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

/* ======================= Features Overview ======================= */
export const FeaturesOverview = () => {
  const { t } = useLang();
  const f = t.featuresOverview;
  return (
    <section id="features" className="border-t border-[var(--border)] bg-white">
      <div className="mx-auto max-w-[1180px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <Reveal><SectionHeading eyebrow={f.eyebrow} title={f.title} accent={f.accent} /></Reveal>
        <Reveal delay={0.05}>
          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3">
            {f.items.map((item, i) => {
              const Icon = OVERVIEW_ICONS[i];
              return (
                <a
                  key={item.t}
                  href={item.href}
                  data-testid={`feature-overview-card-${i}`}
                  className="group flex flex-col rounded-2xl border border-[var(--border)] bg-[var(--surface-2)]/40 p-6 shadow-md-soft transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--blue)]/25 hover:bg-white hover:shadow-lg-soft"
                >
                  <div className="flex items-center gap-3">
                    <span className="grid h-12 w-12 place-items-center rounded-xl bg-[var(--blue-soft)] text-[var(--blue)] transition-transform duration-300 group-hover:scale-[1.06]"><Icon className="h-6 w-6" strokeWidth={1.6} /></span>
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--ink)] font-heading text-xs font-bold text-white">{i + 1}</span>
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-bold leading-snug text-[var(--ink)]">{item.t}</h3>
                  <p className="mt-2 flex-1 text-[14px] leading-relaxed text-[var(--slate)]">{item.d}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--blue)]">
                    {f.more}<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                  </span>
                </a>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ============================ Benefits ============================ */
export const BenefitsSection = () => {
  const { t } = useLang();
  const b = t.benefits;
  return (
    <section id="benefits" className="border-y border-[var(--border)] section-wash">
      <div className="mx-auto max-w-[1180px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <Reveal><SectionHeading eyebrow={b.eyebrow} title={b.title} accent={b.accent} /></Reveal>
        <Reveal delay={0.05}>
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {b.items.map((item, i) => {
              const Icon = BENEFIT_ICONS[i];
              return (
                <div key={item.t} className="group flex flex-col rounded-2xl border border-[var(--border)] bg-white p-6 shadow-md-soft transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--blue)]/25 hover:shadow-lg-soft">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--blue-soft)] text-[var(--blue)] transition-transform duration-300 group-hover:scale-[1.06]"><Icon className="h-5 w-5" strokeWidth={1.7} /></span>
                  <div className="mt-4 font-heading text-[16px] font-bold leading-snug text-[var(--ink)]">{item.t}</div>
                  <div className="mt-2 text-[14px] leading-relaxed text-[var(--slate)]">{item.d}</div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ========================= Differentiation ========================= */
export const DifferentiationSection = () => {
  const { t } = useLang();
  const d = t.differentiation;
  return (
    <section id="differentiation" className="mx-auto max-w-[1180px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <Reveal><SectionHeading eyebrow={d.eyebrow} title={d.title} accent={d.accent} /></Reveal>

      <Reveal delay={0.06}>
        <div className="mx-auto mt-12 max-w-2xl space-y-3">
          {d.rows.map((row, i) => {
            const isProcurmo = i === d.rows.length - 1;
            return (
              <div
                key={row.k}
                className={`flex flex-col gap-1 rounded-2xl border p-5 shadow-md-soft sm:flex-row sm:items-center sm:gap-4 ${
                  isProcurmo
                    ? "border-[var(--blue)]/30 bg-[var(--blue-soft)]"
                    : "border-[var(--border)] bg-white"
                }`}
              >
                <div className={`flex items-center gap-2 sm:w-56 sm:shrink-0 ${isProcurmo ? "text-[var(--blue)]" : "text-[var(--ink)]"}`}>
                  {isProcurmo && <Sparkles className="h-4 w-4" strokeWidth={2} />}
                  <span className="font-heading text-[16px] font-bold">{row.k}</span>
                </div>
                <span className={`text-[15px] ${isProcurmo ? "font-medium text-[var(--ink)]" : "text-[var(--slate)]"}`}>{row.v}</span>
              </div>
            );
          })}
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mx-auto mt-8 flex max-w-2xl flex-wrap justify-center gap-2">
          {d.pillars.map((pill, i) => (
            <span key={pill} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-white px-3 py-1.5 text-[13px] font-medium text-[var(--ink)]">
              <CheckCircle2 className="h-3.5 w-3.5 text-[var(--blue)]" strokeWidth={2} />
              {pill}
            </span>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.14}>
        <p className="mx-auto mt-8 max-w-2xl text-center text-[15px] leading-relaxed text-[var(--slate)] sm:text-base">{d.combine}</p>
        <div className="mx-auto mt-8 flex max-w-2xl items-start justify-center gap-3 rounded-2xl border border-[var(--border)] border-l-4 border-l-[var(--blue)] bg-white p-6 text-center shadow-md-soft">
          <p className="font-heading text-[17px] font-bold leading-snug text-[var(--ink)] sm:text-lg">{d.closer}</p>
        </div>
      </Reveal>
    </section>
  );
};
